import { makeAutoObservable, observable, reaction, runInAction } from 'mobx';
import {
  AbilityBuilder,
  AnyMongoAbility,
  createMongoAbility
} from '@casl/ability';
import type { ProtectedRoutesMap } from '@tools/ui/types';
import { Module } from '@tools/ui/types/generated/graphql';
import { userStore } from './';

class PermissionStore {
  private ability: AnyMongoAbility | null;

  isLoading: boolean;

  modules: Module[] | null;
  moduleRoutesMap: ProtectedRoutesMap | null;
  permissionModules: Record<string, string> | null;

  isHiddenMode = false;

  constructor() {
    makeAutoObservable(this);

    this.isLoading = true;
    this.ability = null;
    this.permissionModules = null;
    this.moduleRoutesMap = null;
    this.modules = null;

    reaction(
      () => observable([this.modules]),
      () => {
        this.defineAbilityForCurrentUser();
      }
    );
  }

  initializeModulePermissions(
    moduleRoutesMap: ProtectedRoutesMap,
    permissionModule: Record<string, string>
  ): void {
    this.moduleRoutesMap = moduleRoutesMap;
    this.permissionModules = permissionModule;
  }

  get getModuleRoutesMap(): ProtectedRoutesMap {
    return this.moduleRoutesMap as ProtectedRoutesMap;
  }

  get getPermissionModules(): Record<string, string> {
    return this.permissionModules as Record<string, string>;
  }

  setIsHiddenMode(isHiddenMode: boolean): void {
    this.isHiddenMode = isHiddenMode;
  }

  setModules(modules: Module[]): void {
    runInAction(() => {
      this.modules = modules;
    });
  }

  toggleLoading(loading = false): void {
    this.isLoading = loading;
  }

  getAbility(): AnyMongoAbility {
    return this.ability as AnyMongoAbility;
  }

  getIsLoading(): boolean {
    return this.isLoading && Boolean(!this.ability);
  }

  defineAbilityForCurrentUser(): void {
    const user = userStore.currentUser;
    if (!this.modules || !user) {
      return;
    }

    this.toggleLoading(true);
    const { can, rules } = new AbilityBuilder(createMongoAbility);

    const permissionModule = this.getPermissionModules as Record<
      string,
      string
    >;
    this.modules.forEach((module) => {
      user.roles.forEach((role) => {
        const isRuleAllowed =
          module.roles?.includes(role._id) && module.name in permissionModule;
        if (isRuleAllowed) {
          can('read', permissionModule[module.name]);
        }
        module.routes?.forEach((route) => {
          if (route.roles?.includes(role._id) && route.path) {
            can('read', route.path);
          }
        });
      });
    });

    this.ability = createMongoAbility(rules);
    this.toggleLoading(false);
  }
}

export default new PermissionStore();
